import { getExpressBaseUrl } from '@/composables/useApi'
import { getEnv } from '@/composables/useEnv'
import Appsignal from '@appsignal/javascript'
import { errorHandler } from '@appsignal/vue'
import { createPinia } from 'pinia'
import { createApp, Directive } from 'vue'
import App from './App.vue'
import './assets/js/DragDropTouch.js' // Allows dragging stickers from the tray on touch screens
import SocketIO from './plugins/Socket.io'
import router from './router'

const pinia = createPinia()

const app = createApp(App).use(pinia).use(router).use(SocketIO, {
  connection: getExpressBaseUrl(),
  options: {}
})

/*
  Global directives
*/
const globalDirective = (
  eventType: string,
  options?: AddEventListenerOptions
): Directive<HTMLElement, (e: Event) => void> => ({
  mounted: (_el, binding) => {
    window.addEventListener(eventType, binding.value, options)
  },
  unmounted: (_el, binding) => {
    window.removeEventListener(eventType, binding.value, options)
  }
})

app.directive('resize', globalDirective('resize'))
app.directive(
  'globalClick',
  globalDirective('click') as Directive<HTMLElement, (e: MouseEvent) => void>
)
app.directive(
  'globalMousedown',
  globalDirective('mousedown') as Directive<
    HTMLElement,
    (e: MouseEvent) => void
  >
)
app.directive(
  'globalKeydown',
  globalDirective('keydown') as Directive<
    HTMLElement,
    (e: KeyboardEvent) => void
  >
)
app.directive(
  'globalKeyup',
  globalDirective('keyup') as Directive<HTMLElement, (e: KeyboardEvent) => void>
)
// passive: false so handlers can preventDefault (e.g. block page scroll while
// dragging the mobile comment sheet)
app.directive(
  'globalTouchmove',
  globalDirective('touchmove', { passive: false }) as Directive<
    HTMLElement,
    (e: TouchEvent) => void
  >
)
app.directive(
  'globalTouchend',
  globalDirective('touchend') as Directive<HTMLElement, (e: TouchEvent) => void>
)

// handles template and watcher errors
// app.config.errorHandler = (err) => {
//   console.log(err)
// }

// enables component init, complile, render and patch performance tracingin the browser devtool performance/timeline panel
// only works in development mode and in browsers that support the performance.mark API
// https://vuejs.org/api/application.html#app-config-performance
// app.config.performance = true

// Provide Appsignal instance for global error handling:
// https://docs.appsignal.com/front-end/integrations/vue.html

if (getEnv('VITE_APP_APPSIGNAL_KEY')) {
  const appsignal = new Appsignal({
    key: getEnv('VITE_APP_APPSIGNAL_KEY'),
    revision: getEnv('VITE_APP_COMMIT_SHA') ?? 'review'
  })
  app.provide('appsignal', appsignal)
  app.config.errorHandler = errorHandler(appsignal, app)
} else {
  app.provide('appsignal', undefined)
}

app.mount('#app')
